import React from 'react';
import { rgba } from 'polished';
import { Link } from 'react-router-dom';
import { FlexDiv} from './containers';
import { TextLabel } from './texts';
import styled, { css } from 'styled-components';
import Tooltip from '@mui/material/Tooltip';
import {calculateSeverityColor } from '../../utils/helpers';
import withStyles from '@mui/styles/withStyles';
import { Snackbar as SnackbarMUI } from '@mui/material';
import Alert from '@mui/material/Alert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { IconButton } from '@mui/material';
import { palette } from './colors';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

export const FullWidthLink = styled(Link)`
    width: 100%;
`;

export function StatusBadge({ text, color, thinner, transparent }) {
    return (
        <FlexDiv>
            <FlexDiv
                py={2}
                px={8}
                borderRadius={16}
                alignCenter
                justifyCenter
                backgroundColor={
                    color && !transparent ? rgba(color, 0.05) : undefined
                }
                border
                borderColor={color ? rgba(color, 0.25) : undefined}
            >
                <TextLabel
                    extraBold={!thinner}
                    color={color}
                    noWrap
                    initialLineHeight
                >
                    {text}
                </TextLabel>
            </FlexDiv>
        </FlexDiv>
    );
}

export const CleanHR = styled.hr`
    margin: 0;
    ${(props) =>
        props.desktopOnly &&
        css`
            @media (max-width: 992px) {
                display: none !important;
            }
        `}
    ${(props) =>
        props.my &&
        css`
            margin-top: ${props.my}px;
            margin-bottom: ${props.my}px;
        `}
`;

export function TooltipWrapper({ tooltipText, showTooltip, children }) {
    if (!showTooltip) return children;
    return (
        <Tooltip title={tooltipText}>
            {/* children need to be wrapped in span tags so tooltip isn't ignored should children be disabled */}
            <span>{children}</span>
        </Tooltip>
    );
}

export function CopyToClipboardWrapper({ copyText, children, onCopied }) {
    return (
        <FlexDiv
            gap={2}
            alignCenter
            onClick={() => {
                navigator.clipboard.writeText(copyText);
                onCopied();
            }}
            link
            $noWrap
        >
            {children}
            <ContentCopyIcon fontSize="small" />
        </FlexDiv>
    );
}

export const LightTooltip = withStyles((theme) => ({
    tooltip: {
        backgroundColor: '#FFFFFF',
        color: '#1D1E1D',
        boxShadow: theme.shadows[1],
        fontSize: 13,
        padding: '1.5em',
        borderRadius: '6px',
        fontWeight: 600,
        lineHeight: '26px',
    },
}))(Tooltip);

export const DarkArrowTooltip = withStyles(() => ({
    tooltip: {
        backgroundColor: 'black',
        color: 'white',
        fontFamily: 'Nunito Sans',
        fontWeight: 400,
        fontSize: '13px',
        lineHeight: '18px',
    },

    arrow: {
        color: 'black',
    },
}))(Tooltip);

export const StyledIconButton = withStyles(() => ({
    root: {
        backgroundColor: 'transparent',
        color: '#777877',
        padding: '0',
        marginRight: (props) => props.marginRight || '16px',

        '&:hover': {
            color: '#3B3C3B',
            backgroundColor: 'transparent',
        },
    },
}))(IconButton);

export const Tag = ({ text, color, backgroundColor }) => {
    return (
        <TextLabel
            color={color || palette.graphite.onyx}
            bold
            uppercase
            backgroundColor={backgroundColor || palette.graphite.gray}
            borderRadius={4}
            //link
            style={{
                padding: '2px 8px',
            }}
        >
            {text}
        </TextLabel>
    );
};

export function Snackbar({
    anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
    autoHideDuration = 6000,
    variant,
    message,
    open,
    onClose,
}) {
    return (
        <SnackbarMUI
            anchorOrigin={anchorOrigin}
            open={open}
            autoHideDuration={autoHideDuration}
            onClose={onClose}
        >
            <Alert onClose={onClose} severity={calculateSeverityColor(variant)}>
                {message}
            </Alert>
        </SnackbarMUI>
    );
}

// tooltipText - specify text that should be shown on hover
// textComponent - i.e. textComponent={<Heading3>{training.name}</Heading3>}
// mobileOnly - show only on mobile
// desktopOnly - show only on desktop
// icon - by default, the icon next to the text is an info icon, but can be a different one, i.e. icon={<ExclamationIcon/>}
export function TextWithTooltipIcon({
    tooltipText,
    textComponent,
    mobileOnly,
    desktopOnly,
    icon,
}) {
    return (
        <FlexDiv
            alignCenter
            gap={4}
            mobileOnly={mobileOnly}
            desktopOnly={desktopOnly}
            $noWrap
        >
            {textComponent}
            <LightTooltip title={tooltipText} placement="bottom">
                {icon || <InfoOutlinedIcon fontSize="small" />}
            </LightTooltip>
        </FlexDiv>
    );
}
